import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ReportType } from '@repo/shared/types';
import { Skeleton } from '@repo/ui/skeleton';
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { lazy, Suspense } from 'react';
import { useRouteLoaderData } from 'react-router';
import { toast } from 'sonner';
import { SelectReportType } from '@/components/report/select-report-type';
import { useONavigate } from '@/hooks/use-organization-navigate';
import { getNextStep, getPrevStep, type WizardLoaderData } from '@/lib/report';
import { trpc } from '@/providers/trpc';

// Lazy-load step components to keep the initial bundle small
const UploadStep = lazy(() =>
  import('@/components/report/upload-step').then((m) => ({ default: m.UploadStep })),
);
const AdditionalStep = lazy(() =>
  import('@/components/report/additional-step').then((m) => ({ default: m.AdditionalStep })),
);
const ClientInformationStep = lazy(() =>
  import('@/components/report/client-information-step').then((m) => ({
    default: m.ClientInformationStep,
  })),
);
const KeyMembersStep = lazy(() =>
  import('@/components/report/key-members-step').then((m) => ({ default: m.KeyMembersStep })),
);
const CompanyInformationStep = lazy(() =>
  import('@/components/report/company-information-step').then((m) => ({
    default: m.CompanyInformationStep,
  })),
);

const StepSkeleton = () => {
  useLocale();
  return (
    <div className="flex flex-col gap-4 p-1">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-2/3" />
    </div>
  );
};

export function ReportCreateWizardPage() {
  useLocale();

  const oNavigate = useONavigate();
  const { report } = useRouteLoaderData('report-create-wizard') as WizardLoaderData;
  const [step, setStep] = useQueryState('step', parseAsString.withDefault('reportType'));
  const [selectedReportType, setSelectedReportType] = useQueryState(
    'srt',
    parseAsStringEnum([ReportType.KYC, ReportType.KYB]).withDefault(ReportType.KYC),
  );

  const { mutate, isPending } = trpc.report.createReport.useMutation({
    onSuccess: (data) => {
      void oNavigate({ to: `/reports/create/${data._id}?step=upload`, options: { replace: true } });
      void setStep('upload');
    },
    onError: () => {
      toast.error(t('auth.failedToCreateReport'), {
        get description() {
          return t('auth.pleaseTryAgainIfTheIssuePersistsContactSupport');
        },
      });
    },
  });

  const goNext = () => {
    const next = getNextStep(report.reportType, step);
    if (next) void setStep(next);
  };

  const goPrev = () => {
    const prev = getPrevStep(report.reportType, step);
    if (prev) void setStep(prev);
  };

  return (
    <Suspense fallback={<StepSkeleton />}>
      {step === 'reportType' && (
        <SelectReportType
          isPending={isPending}
          onContinue={() => mutate({ reportType: selectedReportType })}
          selectedReportType={selectedReportType}
          setSelectedReportType={setSelectedReportType}
        />
      )}

      {step === 'upload' && <UploadStep onContinue={goNext} />}

      {step === 'additional' && <AdditionalStep onContinue={goNext} />}

      {report.reportType === ReportType.KYC && step === 'clientInformation' && (
        <ClientInformationStep />
      )}

      {report.reportType === ReportType.KYB && step === 'keyMembers' && (
        <KeyMembersStep onContinue={goNext} />
      )}

      {report.reportType === ReportType.KYB && step === 'companyInformation' && (
        <CompanyInformationStep />
      )}
    </Suspense>
  );
}
